import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    IconButton,
    TextField,
    useTheme,
    Tooltip,
    InputAdornment,
    Avatar,
    Skeleton,
    Chip,
    Paper,
    Stack,
    Breadcrumbs,
    Link,
} from '@mui/material';
import {
    HolidayVillage,
    Search,
    Business,
    ArrowBack,
    NavigateNext,
    HowToVote,
    Person,
    Lock
} from '@mui/icons-material';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import api from '../../api/axios';

import { Village } from '../../types/village';

const formatCount = (value?: number) => (value ?? 0).toLocaleString('en-IN');

const ConstituencyDetails = () => {
    const { constituencyId } = useParams<{ constituencyId: string }>();
    const navigate = useNavigate();
    const theme = useTheme();

    // State
    const [search, setSearch] = useState('');

    // Fetch Constituency Details (for header)
    const { data: constituency, isLoading: isLoadingConstituency } = useQuery({
        queryKey: ['constituency', constituencyId],
        queryFn: async () => {
            const res = await api.get(`/tenant/constituencies/${constituencyId}`);
            return res.data?.data || res.data;
        },
        enabled: !!constituencyId
    });

    // Fetch Villages for this Constituency
    const { data: villagesData, isLoading: isLoadingVillages } = useQuery({
        queryKey: ['villages', constituencyId, search],
        queryFn: async () => {
            const params: any = { constituency_id: constituencyId, per_page: 100 };
            if (search) params.search = search;
            const res = await api.get('/tenant/villages', { params });
            return res.data;
        },
        enabled: !!constituencyId,
        placeholderData: keepPreviousData,
    });

    const villages = villagesData?.data?.data || villagesData?.data || [];

    const isLoading = isLoadingConstituency || isLoadingVillages;
    const totalVillages = villages.length;

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Breadcrumbs */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
                    <Link component={RouterLink} color="inherit" to="/admin/citizens" sx={{ display: 'flex', alignItems: 'center' }}>
                        Constituencies
                    </Link>
                    <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                        {constituency?.name || 'Loading...'}
                    </Typography>
                </Breadcrumbs>
            </Box>

            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
                    color: 'white',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={() => navigate('/admin/citizens')} sx={{ color: 'white' }}>
                            <ArrowBack />
                        </IconButton>
                        {constituency?.name || <Skeleton width={200} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, opacity: 0.9, ml: 7 }}>
                        Viewing villages within {constituency?.name}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 2, ml: 7 }}>
                        <Chip
                            label={`${totalVillages} Villages`}
                            color="default"
                            variant="outlined"
                            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                        />
                        <Chip
                            icon={<Lock sx={{ color: 'rgba(255,255,255,0.7) !important' }} />}
                            label="Read-only"
                            variant="outlined"
                            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                        />
                    </Stack>
                </Box>
            </Paper>

            {/* Controls Section */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                    Villages List
                </Typography>
                <TextField
                    placeholder="Search villages..."
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 300 }, bgcolor: 'background.paper', borderRadius: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Grid container spacing={3}>
                {isLoading ? (
                    // Loading Skeletons
                    Array.from(new Array(4)).map((_, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                            <Card sx={{ height: 180, borderRadius: 3 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <Skeleton variant="circular" width={40} height={40} />
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width="60%" />
                                            <Skeleton variant="text" width="40%" />
                                        </Box>
                                    </Box>
                                    <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (villages as Village[]).length > 0 ? (
                    (villages as Village[]).map((item) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={item.id}>
                            <Card
                                elevation={2}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'visible',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                        borderColor: theme.palette.primary.main
                                    },
                                    cursor: 'pointer'
                                }}
                                onClick={() => navigate(`/admin/citizens/${constituencyId}/villages/${item.id}`)}
                            >
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: 'rgba(13, 148, 136, 0.1)',
                                                color: '#0d9488',
                                                width: 44,
                                                height: 44,
                                                fontWeight: 'bold',
                                                fontSize: '1.2rem'
                                            }}
                                        >
                                            {item.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                    </Box>

                                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 1.2 }}>
                                        {item.name}
                                    </Typography>

                                    {item.mandal && (
                                        <Chip
                                            label={item.mandal.name}
                                            size="small"
                                            icon={<Business style={{ fontSize: 14 }} />}
                                            sx={{ height: 24, fontSize: '0.75rem' }}
                                        />
                                    )}

                                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                        <Paper variant="outlined" sx={{ p: 1, flex: 1, bgcolor: theme.palette.background.default, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <HowToVote fontSize="small" color="primary" />
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatCount(item.voting_booths_count)} Booths
                                                </Typography>
                                            </Box>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 1, flex: 1, bgcolor: theme.palette.background.default, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Person fontSize="small" color="success" />
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatCount(item.citizens_count)} Citizens
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid size={{ xs: 12 }}>
                        <Paper
                            sx={{
                                p: 6,
                                textAlign: 'center',
                                borderRadius: 4,
                                border: '2px dashed',
                                borderColor: 'divider',
                                bgcolor: 'background.default'
                            }}
                        >
                            <Box sx={{ mb: 2, opacity: 0.5 }}>
                                <HolidayVillage sx={{ fontSize: 60, color: 'text.secondary' }} />
                            </Box>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {search ? `No villages found matching "${search}"` : "No villages in this constituency yet"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Villages are managed by the Super Admin
                            </Typography>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default ConstituencyDetails;
